import React, { useEffect, useMemo, useState, forwardRef } from 'react';
import { optionsWithSelection, SelectSearch } from '~/ui/components';
import { debounce } from 'lodash';
import { useAwaitControl } from 'react-redux-await-control';
import { getTicketReasons } from '~/store/tickets/actions';

export type ReasonSelectProps = {
  value?: number | string | null | undefined;
  onChange?: (value: string) => void;
  allowedLabels?: string[];
};

export const ReasonSelect = forwardRef<HTMLDivElement, ReasonSelectProps>(({ value, onChange, allowedLabels }, ref) => {
  const [currentValue, setCurrentValue] = useState<{ value: string; label: string }>(null);
  const [searchString, setSearchString] = useState('');

  const awaitControl = useAwaitControl(getTicketReasons);
  const isLoading = awaitControl.isRunning();
  const optionsList = awaitControl.result();

  const options = useMemo(() => {
    let list = optionsList;
    if (allowedLabels?.length) {
      const allowed = new Set(allowedLabels.map((l) => l.toLowerCase().trim()));
      list = list?.filter((d) => allowed.has((d.name || '').toLowerCase().trim()));
    }
    if (!searchString?.length) return list;
    return list?.filter((data) => data.name.toLowerCase().includes(searchString.toLowerCase().trim()));
  }, [optionsList, searchString, allowedLabels]);

  const handleOnChange = (option: { value: string; label: string }) => {
    setCurrentValue(option);

    if (onChange) {
      onChange(option?.value);
    }
  };

  const handleSearch = debounce((searchValue) => {
    setSearchString(searchValue);
  }, 500);

  useEffect(() => {
    if (!optionsList?.length) {
      awaitControl.start();
    }
  }, []);

  return (
    <SelectSearch
      ref={ref}
      searchValue={searchString}
      placeholder="Motivo de abertura"
      width={180}
      value={value ? { value: Number(value) } : null}
      onSearch={handleSearch}
      onChange={handleOnChange}
      loading={isLoading}
      options={optionsWithSelection(options, currentValue)}
      onClear={() => setSearchString('')}
    />
  );
});
